'use client'

import { useEffect, useRef } from 'react'

// Types
import type { CountUpProps } from '@/types/components/atoms/animation'

// Length of the count in ms
const DURATION = 1400

// Shows the real number from the first paint; numbers that start below the fold count up from zero when they scroll into view.
// The count writes straight into the element, so it never re-renders React
const CountUpAtom = (props: CountUpProps) => {
	// Props
	const { value, prefix = '', suffix = '', lng } = props

	// Variables
	const ref = useRef<HTMLSpanElement | null>(null)
	const format = new Intl.NumberFormat(lng === 'fa' ? 'fa-IR' : 'en-US')
	const text = (number: number) => `${prefix}${format.format(number)}${suffix}`

	useEffect(() => {
		const element = ref.current

		// Already on screen, or motion turned off: keep the final number
		if (!element || element.getBoundingClientRect().top < window.innerHeight) return
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

		let frame = 0

		const observer = new IntersectionObserver(
			([entry]) => {
				if (!entry.isIntersecting) return
				observer.disconnect()

				const start = performance.now()

				const tick = (now: number) => {
					const progress = Math.min((now - start) / DURATION, 1)

					// Ease out: fast at first, settling at the end
					element.textContent = text(Math.round(value * (1 - Math.pow(1 - progress, 3))))
					if (progress < 1) frame = requestAnimationFrame(tick)
				}

				frame = requestAnimationFrame(tick)
			},
			{ threshold: 0.6 }
		)

		// Start from zero while still out of sight
		element.textContent = text(0)
		observer.observe(element)

		return () => {
			observer.disconnect()
			cancelAnimationFrame(frame)
		}
		// The text helper only depends on these values
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value, prefix, suffix, lng])

	return <span ref={ref}>{text(value)}</span>
}

export default CountUpAtom
